import { useEffect, useState, useRef } from "react";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";
import { createAmbulanceRequest, fetchAmbulance, getCompanyRateCardsData, getCompanyRateCardsByPlanData, submitCancellationReason, updateAmbulanceInfo, sendWhatsappMessage } from "../../pages/actions/actions";
import { ShowAlert } from "../../utils/showAlert";
import { useEmergencyContact } from "../useEmergencyContact/UseEmergencyContact";
import { createWhatsappMessagePayload, currentDateTime } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { ticketUpdateById, getCoveredCities, getSingleBenData, createAmbulanceTripApi, getAmbulanceTrips, updateAmbulanceTripApi } from "../../http";
import { useDispatch } from "react-redux";
import { ticketDataSuccess } from "../../redux/ticketSlice/ticketSlice";
import { PaymentLink } from "../../components/molecules/paymentLink/PaymentLink";
import { useModal } from "../../components/organisms/modalProvider/ModalProvider";
import { setAmbulanceReqInfo } from "../../redux/ambulanceReqSlice/ambulanceReqSlice";
import { ticketUpdateStatusSuccess, setAmbulanceMessage } from "../../redux/ticketUpdateSlice/ticketUpdateSlice";
import { setAmbulanceTripData } from "../../redux/ambulanceTripSlice/ambulanceTripSlice";
import { getDistanceFromLatLonInKm } from "../../utils/distanceCalculation";
// import { hostname } from "os";



export const useAmbulanceBooking = () => {
  const dispatch = useDispatch();
  const { showModal } = useModal();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<number[]>([0, 0, 0, 0]);
  const { getTicketData, getTicketStatus, getTicketUpdateData, getAmbulanceTripData } = UseGetStoreData();
  const { validContacts } = useEmergencyContact();
  const [ambulanceMessageSend, setAmbulanceMessageSend] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string>("");
  const [copySubHeader, setCopySubHeader] = useState<string>("");
  const [copyFields, setCopyFields] = useState<{ label: string; value: string }[]>([]);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [ambulanceDistanceBetween, setAmbulanceDistanceBetween] = useState<string>('');
  const distanceCalculationRef = useRef<number>(0);
  const [ambulanceData, setAmbulanceData] = useState<any>()

  const [ambulancePickupTime, setAmbulancePickupTime] = useState<string>(() => {
    const now = new Date();
    const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    return istTime.toISOString().slice(0, 16);
  });
  const [coveredCities, setCoveredCities] = useState<string[]>();
  const [pocorateCard, setPocoRateCard] = useState<any[]>([]);
  const [selectedAmbulanceType, setSelectedAmbulanceType] = useState<string>("ALS (with Technican)-Tempo/Tata Winger");
  const [rateCards, setRateCards] = useState<any[]>([]);
  const [selectedRateCard, setSelectedRateCard] = useState<string>("");
  const [isRejected, setIsRejected] = useState<boolean>(false);
  const [isDispatched, setIsDispatched] = useState<boolean>(false);
  const [rateCardSource, setRateCardSource] = useState<{ source: 'plan' | 'gmc' | 'company' | 'default'; companyName?: string; planId?: string } | null>(null);
  const [allFetchedCards, setAllFetchedCards] = useState<any[]>([]); // full card objects for lookup on rate card change
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const fetchDistanceBetween = async () => {
      // console.log(getTicketData)
      const { patientLocation, hospitalInfo } = getTicketData;
      if (patientLocation?.geoLocation?.coordinates?.length >= 2 && hospitalInfo?.geoLocation?.coordinates?.length >= 2) {
        const [patientLat, patientLon] = patientLocation.geoLocation.coordinates;
        const [hospitalLat, hospitalLon] = hospitalInfo.geoLocation.coordinates;

        if (patientLat && patientLon && hospitalLat && hospitalLon) {
          const currentCalculationId = ++distanceCalculationRef.current;
          const data = await getDistanceFromLatLonInKm(patientLat, patientLon, hospitalLat, hospitalLon, 0);

          // Only update if this is still the latest calculation
          if (currentCalculationId === distanceCalculationRef.current) {
            setAmbulanceDistanceBetween(data);

            dispatch(setAmbulanceReqInfo({
              pickupLocation: patientLocation,
              dropLocation: hospitalInfo,
              distanceBetween: data
            }));
          }
        }
      }
    }

    fetchDistanceBetween();
    // eslint-disable-next-line
  }, [getTicketData?.patientLocation?.geoLocation?.coordinates, getTicketData?.hospitalInfo?.geoLocation?.coordinates, dispatch]);

  useEffect(() => {
    const fetchCities = async () => {
      const res = await getCoveredCities();
      const cities = res?.data?.data?.map((d: any) => d?.city);
      setCoveredCities(cities);
    }

    const fetchAmbulanceData = async () => {
      if (!getTicketData?._id) return;

      const response = await fetchAmbulance(getTicketData._id);

      if (response?.length > 0) {
        const ambulanceItem = response[0];
        const ambulanceStatuses = ambulanceItem?.ambulanceStatus || [];

        // Update steps based on status count
        const updatedSteps = [0, 0, 0, 0];
        const statusCount = ambulanceStatuses.length;
        for (let i = 0; i < statusCount && i < 4; i++) {
          updatedSteps[i] = 1;
        }
        setCurrentStep(updatedSteps);

        // Update pickup time if available
        const pickupTime = ambulanceItem?.pickupTime;
        if (pickupTime) {
          // Ensure the format is correct for datetime-local input
          const formattedTime = pickupTime.includes('T') ? pickupTime.slice(0, 16) : pickupTime;
          setAmbulancePickupTime(formattedTime);
        }

        if (ambulanceItem?.isCancelled) {
          setIsRejected(true);
        }

        const dispatched = ambulanceStatuses.some((s: any) => s.status === "ambulanceDispatched");
        if (dispatched) setIsDispatched(true);
      }
    }

    fetchCities();
    fetchAmbulanceData();
  }, [getTicketData?._id]);

  useEffect(() => {
    async function fetchData() {
      try {
        const companyId = getTicketData?.companyId;
        if (!companyId) return;

        // Fetch ambulance data first
        let ambulanceResponse = await fetchAmbulance(getTicketData?._id);
        setAmbulanceData(ambulanceResponse);

        // Fetch rate cards: try plan-based first, fallback to company-based
        let response;
        let companyResponse: any[] = []; // always fetch company cards for fallback
        let source: 'plan' | 'company' | 'default' = 'default';
        const companyName = getTicketData?.companyName;
        let resolvedPlanId: string | undefined;
        let isGMCBeneficiary = false;
        const benId = getTicketData?.patient?.id;
        if (benId) {
          try {
            const benRes = await getSingleBenData(benId);
            isGMCBeneficiary = !!(benRes?.data?.data?.empNo && benRes?.data?.data?.clientId);
            const planId = benRes?.data?.data?.insurancePolicies?.[0]?.planId;
            if (planId) {
              const planResponse = await getCompanyRateCardsByPlanData(companyId, planId);
              const hasPlanMatch = planResponse?.some((item: any) => item.planId === planId);
              if (hasPlanMatch) {
                response = planResponse;
                source = 'plan';
                resolvedPlanId = planId;
              }
            }
          } catch (e) {
            // fallback below
          }
        }
        // Always fetch company cards — used as fallback even when plan cards are found
        try {
          companyResponse = await getCompanyRateCardsData(companyId);
        } catch (e) {
          companyResponse = [];
        }
        if (!response || response.length === 0) {
          response = companyResponse;
        }
        const hasMapped = response.some((item: any) => item.isDefaultMapping === false);
        if (source !== 'plan') source = hasMapped ? 'company' : 'default';
        setRateCardSource({ source, companyName, planId: resolvedPlanId });
        // Exclude GMC-specific cards from the general company tier
        // GMC cards are handled separately in the GMC tier below
        const filteredResponse = hasMapped
          ? response.filter((item: any) => item.isDefaultMapping === false && !item.isGMC)
          : response.filter((item: any) => !item.isGMC);

        // Helper: apply tier filter to a list of cards
        const applyTierFilter = (cards: any[], ambulanceCount: number | undefined | null) => {
          const hasTier = cards.some((card: any) => card.minCount !== undefined || card.maxCount !== undefined);
          if (!hasTier || ambulanceCount === undefined || ambulanceCount === null) return cards;

          // First pass: find cards with ranges that match the count
          const rangeMatched = cards.filter((card: any) => {
            const hasMin = card.minCount !== undefined && card.minCount !== null;
            const hasMax = card.maxCount !== undefined && card.maxCount !== null;
            if (!hasMin && !hasMax) return false; // skip no-range cards in first pass
            const cardScope = card.countScope;
            const count = cardScope === 'subscriber'
              ? getTicketData?.ambulanceCount
              : ambulanceCount;
            if (count === undefined || count === null) return false;
            if (hasMin && hasMax) return count >= card.minCount && count < card.maxCount;
            if (hasMin && !hasMax) return count >= card.minCount;
            if (!hasMin && hasMax) return count < card.maxCount;
            return false;
          });

          // If at least one range card matched, show only those
          if (rangeMatched.length > 0) return rangeMatched;

          // No range card matched — fall back to no-range cards (cards without min/max)
          const noRangeCards = cards.filter((card: any) =>
            (card.minCount === undefined || card.minCount === null) &&
            (card.maxCount === undefined || card.maxCount === null)
          );
          return noRangeCards; // return empty if no no-range cards — cascade handles the rest
        };

        const ambulanceCount = getTicketData?.beneficiaryAmbulanceCount;

        // Cascading tier filter: plan → GMC → company → default
        // Plan tier result is already in filteredResponse (plan cards from getCompanyRateCardsByPlanData)
        // or filteredResponse is general company cards (non-GMC) when source !== 'plan'
        let tierFilteredResponse: any[] = [];

        // GMC tier — runs first for GMC beneficiaries (before general company cards)
        // This ensures GMC users get their GMC-specific rate card even when general cards exist
        if (isGMCBeneficiary && source !== 'plan') {
          const gmcCards = companyResponse.filter(
            (item: any) => item.isDefaultMapping === false && item.isGMC === true
          );
          if (gmcCards.length > 0) {
            const gmcTierFiltered = applyTierFilter(gmcCards, ambulanceCount);
            if (gmcTierFiltered.length > 0) {
              tierFilteredResponse = gmcTierFiltered;
              setRateCardSource({ source: 'gmc', companyName });
            }
          }
        }

        // Plan tier / general company tier — runs when GMC tier didn't produce results
        if (tierFilteredResponse.length === 0) {
          tierFilteredResponse = applyTierFilter(filteredResponse, ambulanceCount);
        }

        // Plan fallthrough for GMC users — plan cards didn't match, try GMC cards first
        if (tierFilteredResponse.length === 0 && source === 'plan' && isGMCBeneficiary) {
          const gmcCards = companyResponse.filter(
            (item: any) => item.isDefaultMapping === false && item.isGMC === true
          );
          if (gmcCards.length > 0) {
            const gmcTierFiltered = applyTierFilter(gmcCards, ambulanceCount);
            if (gmcTierFiltered.length > 0) {
              tierFilteredResponse = gmcTierFiltered;
              setRateCardSource({ source: 'gmc', companyName });
            }
          }
        }

        // Plan fallthrough for non-GMC users (or GMC users whose GMC cards also didn't match)
        // Try general company cards (exclude GMC)
        if (tierFilteredResponse.length === 0 && source === 'plan') {
          const companyMapped = companyResponse.filter((item: any) => item.isDefaultMapping === false && !item.isGMC);
          if (companyMapped.length > 0) {
            const companyTierFiltered = applyTierFilter(companyMapped, ambulanceCount);
            if (companyTierFiltered.length > 0) {
              tierFilteredResponse = companyTierFiltered;
              setRateCardSource({ source: 'company', companyName, planId: resolvedPlanId });
            }
          }
        }

        if (tierFilteredResponse.length === 0) {
          // Still nothing — fall back to global DEFAULT cards
          const defaultCards = companyResponse.filter((card: any) => card.isDefaultMapping === true);
          tierFilteredResponse = defaultCards.length > 0 ? defaultCards : filteredResponse;
          if (defaultCards.length > 0) {
            setRateCardSource({ source: 'default', companyName, planId: resolvedPlanId });
          }
        }

        const rateCardOptions = tierFilteredResponse.map((item: any) => ({
          value: item.rateCardName,
          label: item.rateCardName
        }));
        setRateCards(rateCardOptions);
        setAllFetchedCards(tierFilteredResponse);

        // Auto-select first card that matches the count range
        let defaultCard = tierFilteredResponse[0] || null;
        let defaultSortedTypes: { value: string; label: string }[] = [];
        if (defaultCard) {
          setSelectedRateCard(defaultCard.rateCardName);

          if (defaultCard.ambulanceTypes && Array.isArray(defaultCard.ambulanceTypes)) {
            defaultSortedTypes = defaultCard.ambulanceTypes
              .map((type: string) => ({ value: type, label: type }))
              .sort((a: { value: string }, b: { value: string }) => {
                if (/BLS/i.test(a.value)) return -1;
                if (/BLS/i.test(b.value)) return 1;
                if (/ALS/i.test(a.value)) return -1;
                if (/ALS/i.test(b.value)) return 1;
                return 0;
              });

            setPocoRateCard(defaultSortedTypes);
            if (defaultSortedTypes[0]?.value) {
              setSelectedAmbulanceType(String(defaultSortedTypes[0].value));
            }
          }
        }

        // Override with existing ambulance data if available
        let finalRateCard = defaultCard?.rateCardName || "";
        let finalAmbulanceType = defaultSortedTypes[0]?.value || "";

        if (ambulanceResponse?.[0]?.ambulanceType) {
          finalAmbulanceType = String(ambulanceResponse[0].ambulanceType);
          setSelectedAmbulanceType(finalAmbulanceType);
        }
        const savedRateCardName = ambulanceResponse?.[0]?.rateCardName || ambulanceResponse?.[0]?.ambulanceProvider;
        if (savedRateCardName) {
          finalRateCard = String(savedRateCardName);
          setSelectedRateCard(finalRateCard);

          const savedCard = tierFilteredResponse.find((item: any) => item.rateCardName === savedRateCardName)
            || response.find((item: any) => item.rateCardName === savedRateCardName);
          if (savedCard && savedCard.ambulanceTypes) {
            const sortedTypes = savedCard.ambulanceTypes
              .map((type: string) => ({ value: type, label: type }))
              .sort((a: { value: string }, b: { value: string }) => {
                if (/BLS/i.test(a.value)) return -1;
                if (/BLS/i.test(b.value)) return 1;
                if (/ALS/i.test(a.value)) return -1;
                if (/ALS/i.test(b.value)) return 1;
                return 0;
              });

            setPocoRateCard(sortedTypes);
          }
        }

        // Sync selections to Redux so PaymentLink can read them
        dispatch(ticketUpdateStatusSuccess({
          selectedPocoRateCard: finalRateCard,
          selectedAmbulanceType: finalAmbulanceType
        }));
      } catch (err) {
        console.log(err);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketData?.companyId]);

  useEffect(() => {
    const hasSharedMessage = getTicketData?.notes?.some(
      (note: any) => note.message === "Pickup and drop location shared to agent"
    );
    if (hasSharedMessage) {
      setAmbulanceMessageSend(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save ambulanceType, rateCard, pickupTime to DB whenever they change
  // after the ambulance record already exists (i.e. after first Ambulance Message click)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    if (!getTicketData?._id || !ambulanceData?.length) return;
    if (!selectedAmbulanceType && !selectedRateCard && !ambulancePickupTime) return;

    updateAmbulanceInfo(getTicketData._id, {
      ambulanceType: selectedAmbulanceType,
      ambulanceProvider: selectedRateCard,
      pickupTime: ambulancePickupTime,
    }).catch((err) => console.error("Auto-save failed:", err));

    // Keep hospital-service trip in sync if trip exists in store
    const tripId = getAmbulanceTripData?._id;
    if (tripId && selectedAmbulanceType) {
      updateAmbulanceTripApi(tripId, { ambulanceType: selectedAmbulanceType })
        .catch(() => { /* non-blocking */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAmbulanceType, selectedRateCard, ambulancePickupTime]);

  // Sync from Redux - when payment link modal changes rate card/type, update local state
  useEffect(() => {
    if (getTicketUpdateData?.selectedPocoRateCard && getTicketUpdateData.selectedPocoRateCard !== selectedRateCard) {
      setSelectedRateCard(getTicketUpdateData.selectedPocoRateCard);
    }
    if (getTicketUpdateData?.selectedAmbulanceType && getTicketUpdateData.selectedAmbulanceType !== selectedAmbulanceType) {
      setSelectedAmbulanceType(getTicketUpdateData.selectedAmbulanceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketUpdateData?.selectedPocoRateCard, getTicketUpdateData?.selectedAmbulanceType]);

  const isCityCovered = (pickupCity: string) => {
    if (pickupCity === 'N/A' || !pickupCity) return "No Data Present";
    const index: number = coveredCities?.findIndex((city: string) => pickupCity.includes(city)) || -1;
    return index !== -1 ? coveredCities?.[index] : "Not available in any covered city";
  }

  const getSelectedAssistanceReq = () => {
    const assistanceReq = getTicketUpdateData?.typeOfAssistanceRequired || ambulanceData?.[0]?.typeOfAssistanceRequired;
    const selectedItems = [];

    if (assistanceReq?.wheelChair) selectedItems.push("wheelChair");
    if (assistanceReq?.stretcher) selectedItems.push("stretcher");
    if (assistanceReq?.paramedic) selectedItems.push("paramedic");
    if (assistanceReq?.additionalHelp) selectedItems.push("additionalHelp");
    if (assistanceReq?.DoctorOnBoard) selectedItems.push("DoctorOnBoard");

    return selectedItems.length > 0 ? selectedItems.join(", ") : "N/A";
  };

  // Builds ambulance message text from current state — pure, no side effects.
  const buildAmbulanceMessageText = () => {
    const hopitalName = getTicketData?.hospitalInfo?.hospitalName || ambulanceData?.[0]?.dropLocation?.[0]?.hospitalName;
    const pickupCoords = getTicketData?.patientLocation?.geoLocation?.coordinates || ambulanceData?.[0]?.pickupLocation?.location?.coordinates;
    const dropCoords = getTicketData?.hospitalInfo?.geoLocation?.coordinates || ambulanceData?.[0]?.dropLocation?.[0]?.location?.coordinates;
    const pickupMapLink = pickupCoords ? `https://www.google.com/maps?q=${pickupCoords[0]},${pickupCoords[1]}` : "N/A";
    const dropMapLink = dropCoords ? `https://www.google.com/maps?q=${dropCoords[0]},${dropCoords[1]}` : "N/A";

    const beneficiary = getTicketData?.patient?.fullName || getTicketData?.ticketRaisedBy?.fullName || "N/A";
    const beneficiaryUId = getTicketData?.patient?.id || getTicketData?.ticketRaisedBy?.benId || "N/A";
    const contactperson = getTicketData?.poc?.fullName || "N/A";
    const contactnumber = getTicketData?.poc?.mobile || "N/A";
    const patientCondition = [
      ...(getTicketData?.currentMedicalStatusResponse?.currentCondition || []),
      ...(getTicketData?.currentMedicalStatusResponse?.otherDescription ? [getTicketData.currentMedicalStatusResponse.otherDescription] : [])
    ].join(", ") || "N/A";
    const ambulancetype = selectedAmbulanceType || ambulanceData?.[0]?.ambulanceType || "N/A";
    const requirement = getSelectedAssistanceReq();
    const noofhelper = getTicketUpdateData?.typeOfAssistanceRequired?.additionalHelp
      ? (getTicketUpdateData?.typeOfAssistanceRequired?.numberOfHelpers || ambulanceData?.[0]?.typeOfAssistanceRequired?.numberofHelper || "N/A")
      : "N/A";
    const pickupaddress = getTicketData?.patientLocation?.address || ambulanceData?.[0]?.pickupLocation?.address || "N/A";
    const dropaddress = `${hopitalName || ""}${getTicketData?.hospitalInfo?.address}` || `${hopitalName || ""}${ambulanceData?.[0]?.dropLocation?.[0]?.address}` || "N/A";
    const pickuptime = (() => {
      const pickupDateTime = new Date(ambulancePickupTime || ambulanceData?.[0]?.pickupTime);
      const now = new Date();
      if (pickupDateTime <= now) return "Immediately";
      return pickupDateTime.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "medium",
        timeStyle: "short",
      });
    })();

    const fields = [
      { label: "Booking ID", value: beneficiaryUId },
      { label: "Contact Person", value: `${contactperson} | ${contactnumber}` },
      { label: "Patient Condition", value: patientCondition },
      { label: "Ambulance Type", value: ambulancetype },
      { label: "Requirements", value: requirement },
      { label: "No. of Helpers", value: String(noofhelper) },
      { label: "Pickup Address", value: pickupaddress },
      { label: "Pickup Map", value: pickupMapLink },
      { label: "Drop Address", value: dropaddress },
      { label: "Drop Map", value: dropMapLink },
      { label: "Pickup Time", value: pickuptime },
    ];
    const subHeader = `An ambulance booking has been created for ${beneficiary} based on a service request.`;
    const messageText = `*Ambulance Request from Pococare*\n${subHeader}\n\n` + fields.map(f => `${f.label}: ${f.value}`).join("\n\n");
    return { messageText, subHeader, fields };
  };

  // Auto-dispatch message to Redux whenever key ticket/booking data changes,
  // so providers screen always has the latest text without requiring the agent
  // to click "Ambulance Message" first.
  useEffect(() => {
    if (!getTicketData?._id) return;
    const { messageText } = buildAmbulanceMessageText();
    dispatch(setAmbulanceMessage(messageText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getTicketData?._id,
    getTicketData?.patient?.fullName,
    getTicketData?.poc?.fullName,
    getTicketData?.poc?.mobile,
    getTicketData?.patientLocation?.address,
    getTicketData?.hospitalInfo?.address,
    getTicketData?.hospitalInfo?.hospitalName,
    getTicketData?.currentMedicalStatusResponse,
    selectedAmbulanceType,
    ambulancePickupTime,
    getTicketUpdateData?.typeOfAssistanceRequired,
  ]);

  const handleSendAmbulanceReq = async () => {
    setAmbulanceMessageSend(true);
    const hopitalName = getTicketData?.hospitalInfo?.hospitalName || ambulanceData?.[0]?.dropLocation?.[0]?.hospitalName;
    const pickupCoords = getTicketData?.patientLocation?.geoLocation?.coordinates || ambulanceData?.[0]?.pickupLocation?.location?.coordinates;
    const dropCoords = getTicketData?.hospitalInfo?.geoLocation?.coordinates || ambulanceData?.[0]?.dropLocation?.[0]?.location?.coordinates;
    const pickupMapLink = pickupCoords ? `https://www.google.com/maps?q=${pickupCoords[0]},${pickupCoords[1]}` : "N/A";
    const dropMapLink = dropCoords ? `https://www.google.com/maps?q=${dropCoords[0]},${dropCoords[1]}` : "N/A";
    const whatsappMessagePayload = {
      template: "AMBULANCE_MESSAGE_PHA_2026",
      data: {
        to: [getTicketData?.agent?.mobile], //instead here can we take it from cookie???
        beneficiary: getTicketData?.patient?.fullName || getTicketData?.ticketRaisedBy?.fullName || "N/A",
        beneficiaryUId: getTicketData?.patient?.id || getTicketData?.ticketRaisedBy?.benId || "N/A",
        contactperson: `${getTicketData?.poc?.fullName || "N/A"}`,
        contactnumber: getTicketData?.poc?.mobile || "N/A",
        patientCondition: [
          ...(getTicketData?.currentMedicalStatusResponse?.currentCondition || []),
          ...(getTicketData?.currentMedicalStatusResponse?.otherDescription ? [getTicketData.currentMedicalStatusResponse.otherDescription] : [])
        ].join(", ") || "N/A",
        ambulancetype: selectedAmbulanceType || ambulanceData?.[0]?.ambulanceType || "N/A",
        requirement: getSelectedAssistanceReq(),
        noofhelper: getTicketUpdateData?.typeOfAssistanceRequired?.additionalHelp
          ? (getTicketUpdateData?.typeOfAssistanceRequired?.numberOfHelpers || ambulanceData?.[0]?.typeOfAssistanceRequired?.numberofHelper || "N/A")
          : "N/A",
        pickupaddress: getTicketData?.patientLocation?.address || ambulanceData?.[0]?.pickupLocation?.address || "N/A",
        pickuppoint: pickupMapLink,
        dropaddress: `${hopitalName || ""}${getTicketData?.hospitalInfo?.address}` || `${hopitalName || ""}${ambulanceData?.[0]?.dropLocation?.[0]?.address}` || "N/A",
        droppoint: dropMapLink,
        pickupLocation: getTicketData?.patientLocation?.geoLocation?.coordinates?.join(",") || ambulanceData?.[0]?.pickupLocation?.location?.coordinates?.join("") || "N/A",
        pickuptime: (() => {
          const pickupDateTime = new Date(ambulancePickupTime || ambulanceData[0]?.pickupTime);
          const now = new Date();
          if (pickupDateTime <= now) {
            return "Immediately";
          }
          return pickupDateTime.toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            dateStyle: "medium",
            timeStyle: "short",
          });
        })(),
        // isBeneficiaryConcious:
        //   getTicketData?.currentMedicalStatusResponse?.isPatientConscious ||
        //   "N/A",
        // beneficiaryLocation: getTicketData?.patientLocation?.address,
        // latlon:
        //   getTicketData.patientLocation?.geoLocation?.coordinates?.join(",") ||
        //   "N/A",
        // isReachableByAmbulance:
        //   getTicketData?.patientLocation?.isReachableByAmbulance,
        // isLiftAvailable: getTicketData?.patientLocation?.isLiftAvailable,
        // additionalRequiurements: getSelectedAssistanceReq() || "N/A",
        // hospital: (getTicketData?.hospitalInfo?.address) ? `${getTicketData?.hospitalInfo?.hospitalName}, ${getTicketData?.hospitalInfo?.address}` : "N/A",
      },
    };
    const fields = [
      { label: "Booking ID", value: whatsappMessagePayload.data.beneficiaryUId },
      { label: "Contact Person", value: `${whatsappMessagePayload.data.contactperson} | ${whatsappMessagePayload.data.contactnumber}` },
      { label: "Patient Condition", value: whatsappMessagePayload.data.patientCondition },
      { label: "Ambulance Type", value: whatsappMessagePayload.data.ambulancetype },
      { label: "Requirements", value: whatsappMessagePayload.data.requirement },
      { label: "No. of Helpers", value: String(whatsappMessagePayload.data.noofhelper) },
      { label: "Pickup Address", value: whatsappMessagePayload.data.pickupaddress },
      { label: "Pickup Map", value: whatsappMessagePayload.data.pickuppoint },
      { label: "Drop Address", value: whatsappMessagePayload.data.dropaddress },
      { label: "Drop Map", value: whatsappMessagePayload.data.droppoint },
      { label: "Pickup Time", value: whatsappMessagePayload.data.pickuptime },
    ];
    const beneficiaryName = whatsappMessagePayload.data.beneficiary;
    const subHeader = `An ambulance booking has been created for ${beneficiaryName} based on a service request.`;
    const messageText = `*Ambulance Request from Pococare*\n${subHeader}\n\n` + fields.map(f => `${f.label}: ${f.value}`).join("\n\n");
    setCopySubHeader(subHeader);
    setCopyFields(fields);
    setCopyMessage(messageText);
    dispatch(setAmbulanceMessage(messageText)); // store for WhatsApp button on providers screen
    setIsCopyModalOpen(true);
    const payload = {
      notes: [...getTicketData?.notes, {
        date: currentDateTime(),
        message: "Pickup and drop location shared to agent"
      }]
    }
    await updateTicket(getTicketData._id, payload, "")
    const updatedAmbulance = await handleAmbulanceBooking();
    if (updatedAmbulance) {
      const refreshedData = await fetchAmbulance(getTicketData._id);
      setAmbulanceData(refreshedData);
    }

    // Create ambulance trip record in hospital service — non-blocking, idempotent
    try {
      const ticketId = getTicketData._id?.toString();
      // Check if an active (non-completed, non-cancelled) trip already exists
      const existingRes = await getAmbulanceTrips({ ticketId });
      const existingTrips: any[] = existingRes.data?.data ?? [];
      const hasActive = existingTrips.some(
        (t: any) => t.serviceStatus !== "Completed" && t.serviceStatus !== "Cancelled",
      );
      if (!hasActive) {
        const newTripRes = await createAmbulanceTripApi({
          ticketId,
          ambulanceBookingRef: ticketId,
          ambulanceType: selectedAmbulanceType || ambulanceData?.[0]?.ambulanceType || "N/A",
          scheduledPickupTime: ambulancePickupTime || ambulanceData?.[0]?.pickupTime || undefined,
          pickupLocation: {
            address: getTicketData?.patientLocation?.address || "N/A",
            coordinates: pickupCoords,
          },
          dropLocation: [{
            address:
              `${hopitalName || ""}${getTicketData?.hospitalInfo?.address || ""}` || "N/A",
            coordinates: dropCoords,
          }],
          patient: {
            benId: getTicketData?.patient?.id || getTicketData?.patient?._id || "N/A",
            name: getTicketData?.patient?.fullName || getTicketData?.ticketRaisedBy?.fullName || "N/A",
            mobile: {
              countryCode: "+91",
              number: getTicketData?.poc?.mobile || "N/A",
            },
          },
        });
        // Trip just created — store it directly without an extra fetch
        const newTrip = newTripRes.data?.data ?? newTripRes.data ?? null;
        if (newTrip?._id) dispatch(setAmbulanceTripData(newTrip));
      } else {
        // Trip already exists — load it into Redux so syncTripPickup/syncTripDrop can use its _id
        const activeTrip = existingTrips.find(
          (t: any) => t.serviceStatus !== "Completed" && t.serviceStatus !== "Cancelled"
        ) ?? existingTrips[0];
        if (activeTrip?._id) dispatch(setAmbulanceTripData(activeTrip));
      }
    } catch {
      console.warn("Failed to create ambulance trip record — non-blocking");
    }
  };
  const closeCopyModal = () => setIsCopyModalOpen(false);
  const handleStepChanges = async (step: any) => {
    if (!ambulanceMessageSend) {
      ShowAlert("warning", "Please Send Ambulance Message First");
      return;
    }
    const updatedSteps = [...currentStep];
    updatedSteps[step - 1] = 1;
    setCurrentStep(updatedSteps);
    try {
      switch (step) {
        case 1:
          await handleTicketUpdate("dispatch");
          ShowAlert("success", "Ambulance Dispatched Successfully");
          setIsDispatched(true);
          break;
        case 2:
          await handleTicketUpdate("pickupLocation");
          ShowAlert("success", "Arrived at Pickup Location");
          break;
        case 3:
          await handleTicketUpdate("pickupPatient");
          ShowAlert("success", "Patient Picked Up Successfully");
          break;
        case 4:
          await handleTicketUpdate("dropPatient");
          ShowAlert("success", "Patient Dropped Successfully");
          break;
        default:
          throw new Error("Unknown action");
      }
      const refreshed = await fetchAmbulance(getTicketData._id);
      setAmbulanceData(refreshed);
    } catch (e) {
      ShowAlert("warning", "Some Technical Issue in Tracking Ambulance")
    }
  };
  const handleTicketUpdate = async (action: string) => {
    if (getTicketData.ticketStatus === "New") {
      return alert("Please Select Start Emergency");
    }
    let commonData;
    if (action !== "resolve")
      commonData = {
        to: [...validContacts],
        beneficiary: getTicketData?.patient?.fullName || "N/A",
      };

    let waMessagePayload: any = {};
    let updateTicketPayload: any = {};

    switch (action) {
      case "dispatch":
        waMessagePayload = createWhatsappMessagePayload({
          templateName: "NOTIFY_EC_ON_AMBULANCE_BOOKING_WITHOUT_TRACKING",
          data: {
            ...commonData,
            trackingLink: `Please click on the link below to track the ambulance:`,
          },
        });
        updateTicketPayload = {
          ambulanceInfo: { status: "ambulanceDispatched" }, notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "ambulanceDispatched"
          }]
        };
        break;

      case "pickupLocation":
        waMessagePayload = createWhatsappMessagePayload({
          templateName: "NOTIFY_EC_ON_AMBULANCE_ARRIVAL",
          data: commonData,
        });
        updateTicketPayload = {
          ambulanceInfo: { status: "ambulanceArrivedPickupLocation" },
          notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "ambulanceArrivedPickupLocation"
          }]
        };
        break;

      case "pickupPatient":
        waMessagePayload = createWhatsappMessagePayload({
          templateName: "NOTIFY_EC_ON_PICKUP",
          data: {
            ...commonData,
            hospital: getTicketData?.hospitalInfo?.address,
          },
        });
        updateTicketPayload = {
          ambulanceInfo: { status: "patientPicked" }
          , notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "patientPicked"
          }]
        };
        break;
      case "dropPatient":
        waMessagePayload = createWhatsappMessagePayload({
          templateName: "NOTIFY_EC_ON_DROPOFF",
          data: {
            ...commonData,
            hospital: getTicketData?.hospitalInfo?.address,
          },
        });
        updateTicketPayload = {
          ambulanceInfo: { status: "patientDropped" },
          notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "patientDropped"
          }]
        };
        break;
      case "resolve":
        updateTicketPayload = {
          ticketStatus: "Completed",
          Demo: false,
          notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "Ticket Resolved - Demo"
          }]
        };
        await updateTicket(getTicketData._id, updateTicketPayload, "");
        ShowAlert("success", "Emergency Resolved Successfully")
        navigate("/");
        return;
      case "demo":
        updateTicketPayload = {
          ticketStatus: "Completed",
          Demo: true,
          notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "Ticket Resolved"
          }]
        };
        await updateTicket(getTicketData._id, updateTicketPayload, "");
        ShowAlert("success", "Emergency Resolved Successfully")
        navigate("/");
        return;
      case "bookAmbulance":
        updateTicketPayload = {
          currentMedicalStatusResponse: {
            ...getTicketData?.currentMedicalStatusResponse,
            isAmbulanceRequired: "Yes",
          },
          notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "Choose to Book Ambulance"
          }]
        };
        await updateTicket(getTicketData._id, updateTicketPayload, "");
        navigate("/logistics");
        return;
      case "ambulanceSendStatusUpdate":
        updateTicketPayload = {
          notes: [...getTicketData?.notes, {
            date: currentDateTime(),
            message: "Ambulance Details Shared"
          }]
        };
        await updateTicket(getTicketData._id, updateTicketPayload, "");
        ShowAlert("success", "Emergency Resolved Successfully")
        navigate("/");
        return;
      default:
        throw new Error("Unknown action");
    }
    // console.log('waMessagePayload:', waMessagePayload);
    await updateTicket(
      getTicketData._id,
      updateTicketPayload,
      waMessagePayload,
    );
    if (updateTicketPayload.ambulanceInfo) {
      let payloadData = {
        ...(updateTicketPayload.ambulanceInfo.status === "patientDropped" && { serviceStatus: "Fulfilled" }),
        ambulanceStatus: {
          status: updateTicketPayload.ambulanceInfo.status || "",
          timestamp: new Date().toISOString()
        }
      }
      await updateAmbulanceInfo(getTicketData._id, payloadData);
    }
  };
  const updateTicket = async (
    id: any,
    ticketPayload: any,
    waMessagePayload: any
  ) => {
    try {
      if (id) {
        const response = await ticketUpdateById(id, ticketPayload);
        if (response) {
          dispatch(ticketDataSuccess(response.data));
        }
      }
      if (waMessagePayload && Object.keys(waMessagePayload).length > 0) {
        await sendWhatsappMessage(waMessagePayload);
      }
    } catch (e: any) {
      return alert(
        e?.response?.data?.message || "Some Technical Issue in Ticket Updating"
      );
    }
  };
  const sendAMbulancePaymentLink = () => {
    showModal(<PaymentLink component="Ambulance" />, "Ambulance")
  }

  const handleAmbulanceType = (e: React.ChangeEvent<HTMLSelectElement> | string) => {
    let value: string;
    if (typeof e === 'string') {
      value = e;
    } else {
      value = e?.target?.value || '';
    }
    setSelectedAmbulanceType(value);
    dispatch(ticketUpdateStatusSuccess({ selectedAmbulanceType: value }));
  }

  const handleRateCardChange = async (value: string) => {
    setSelectedRateCard(value);
    dispatch(ticketUpdateStatusSuccess({ selectedPocoRateCard: value }));

    // Look up the card from already-fetched data — no re-fetch needed
    const selectedCard = allFetchedCards.find((item: any) => item.rateCardName === value);

    if (selectedCard && selectedCard.ambulanceTypes && Array.isArray(selectedCard.ambulanceTypes)) {
      const sortedTypes = selectedCard.ambulanceTypes
        .map((type: string) => ({ value: type, label: type }))
        .sort((a: { value: string }, b: { value: string }) => {
          if (/BLS/i.test(a.value)) return -1;
          if (/BLS/i.test(b.value)) return 1;
          if (/ALS/i.test(a.value)) return -1;
          if (/ALS/i.test(b.value)) return 1;
          return 0;
        });

      setPocoRateCard(sortedTypes);
      if (sortedTypes[0]?.value) {
        const firstType = String(sortedTypes[0].value);
        setSelectedAmbulanceType(firstType);
        dispatch(ticketUpdateStatusSuccess({ selectedAmbulanceType: firstType }));
      }
    }
  }

  const handleEdit = (type: string) => {

    navigate("/logistics", { state: { from: `ambulanceBooking-${type}` } });

  };
  const handleAmbulanceBooking = async () => {


    try {
      const payload = {
        ticket_id: `${getTicketData?._id}`,
        serviceStatus: "Requested",
        ambulanceStatus: null,
        ambulanceType: selectedAmbulanceType,
        ambulanceProvider: selectedRateCard,
        pickupLocation: {
          address: getTicketData?.patientLocation?.address,
          label: getTicketData?.patientLocation?.label || "N/A",
          ...(getTicketData?.patientLocation?.hospitalName && {
            hospitalName: getTicketData.patientLocation.hospitalName,
          }),
          location: getTicketData?.patientLocation?.geoLocation,
          city: getTicketData?.patientLocation?.city || "N/A",
        },
        dropLocation: [{
          address: getTicketData?.hospitalInfo?.address,
          label: getTicketData?.hospitalInfo?.hospitalName ? "Hospital" : "custom",
          ...(getTicketData?.hospitalInfo?.hospitalName && {
            hospitalName: getTicketData.hospitalInfo.hospitalName,
          }),
          location: getTicketData?.hospitalInfo?.geoLocation
        }],
        typeOfAssistanceRequired: {
          wheelChair: getTicketUpdateData?.typeOfAssistanceRequired?.wheelChair || false,
          stretcher: getTicketUpdateData?.typeOfAssistanceRequired?.stretcher || false,
          paramedic: getTicketUpdateData?.typeOfAssistanceRequired?.paramedic || false,
          additionalHelp: getTicketUpdateData?.typeOfAssistanceRequired?.additionalHelp || false,
          numberofHelper: getTicketUpdateData?.typeOfAssistanceRequired?.numberOfHelpers || 0,
          DoctorOnBoard: getTicketUpdateData?.typeOfAssistanceRequired?.DoctorOnBoard || false
        },
        pickupTime: ambulancePickupTime,
        tripDistance: ambulanceDistanceBetween,
        ...(getTicketUpdateData?.selectedAmbulanceProvider && {
          selectedProvider: {
            providerId: getTicketUpdateData.selectedAmbulanceProvider.id,
            providerName: getTicketUpdateData.selectedAmbulanceProvider.name,
            primaryContact: getTicketUpdateData.selectedAmbulanceProvider.primaryContact,
          },
        }),
      }


      const response = await fetchAmbulance(getTicketData?._id)
      if (response?.length === 0) {
        await createAmbulanceRequest(payload);
      } else {
        const { ambulanceStatus: _, ...updatePayload } = payload;
        await updateAmbulanceInfo(getTicketData?._id, updatePayload)
      }
      //this clear the redux store that clear the assistance required data 
      // if (response1) {
      //   dispatch(ticketUpdateStatusSuccess({ typeOfAssistanceRequired: undefined }));
      // }
      return response;
    } catch (error) {

      throw error;
    }
  }
  const handleRejectAmbulance = async (reason: string, isChargeable: boolean) => {
    try {
      await submitCancellationReason(getTicketData._id, reason, isChargeable);
      const payload = {
        notes: [...getTicketData?.notes, {
          date: currentDateTime(),
          message: `Ambulance Rejected: ${reason}`
        }]
      };
      await updateTicket(getTicketData._id, payload, "");
      setIsRejected(true);
      ShowAlert("success", "Rejection reason saved");
    } catch (e) {
      ShowAlert("warning", "Failed to save rejection reason");
    }
  };

  return {

    selectedAmbulanceType,
    currentStep,
    setCurrentStep,
    handleSendAmbulanceReq,
    ambulanceMessageSend,
    handleTicketUpdate,
    handleStepChanges,
    getTicketStatus,
    sendAMbulancePaymentLink,
    ambulanceDistanceBetween,
    handleEdit,
    handleAmbulanceBooking,
    ambulancePickupTime,
    setAmbulancePickupTime,
    isCityCovered,
    pocorateCard,
    handleAmbulanceType,
    ambulanceData,
    rateCards,
    selectedRateCard,
    handleRateCardChange,
    rateCardSource,
    copyMessage,
    copySubHeader,
    copyFields,
    isCopyModalOpen,
    closeCopyModal,
    handleRejectAmbulance,
    isRejected,
    isDispatched,
  };
};
